import {
  PageContentEditor,
  type PageContentEditorBlock,
} from '@app/components/blocks/pageContentEditor/PageContentEditor'
import { PhaseType } from '@schema/phase'
import {
  InitiativeCreationStep,
  initiativeCreationSteps,
} from '@app/pages/dashboard/initiative/shared/workflow'
import { CreationGuide } from '../blocks/CreationGuide'
import { useEffect, useMemo, useState } from 'react'
import {
  isOverviewPageContentStepValid,
  mapOverviewTextBlocks,
} from '@app/pages/dashboard/initiative/shared/initiativeOverviewTextBlocks'
import { useInitiativePageContentBlocksSave } from '@app/pages/dashboard/initiative/shared/useInitiativePageContentBlocksSave'
import { InitiativeWithId } from '../../shared/initiativeTypes'
import Column from '@app/components/layout/Column'
import { Text } from '@app/components/core/typography/Text'
import { useTranslations } from '@i18n/useLanguage'
import { enrichWithLinks } from '@i18n/localizeHelper'

export const PageContentStep = ({
  initiative,
  selectedPhaseType,
  completeStep,
  uncompleteStep,
}: {
  initiative: InitiativeWithId
  selectedPhaseType: PhaseType
  completeStep: (step: InitiativeCreationStep) => void
  uncompleteStep: (step: InitiativeCreationStep) => void
}) => {
  const t = useTranslations().app.dashboard.initiative.creation
  const placeholders = t.pageContentPlaceholders
  const serverBlocks = useMemo(() => {
    return mapOverviewTextBlocks(initiative.contentBlocks, [
      placeholders.mainBlock,
    ])
  }, [initiative.contentBlocks, placeholders])

  const [blocks, setBlocks] = useState<PageContentEditorBlock[]>(serverBlocks)

  useEffect(() => {
    setBlocks(serverBlocks)
  }, [serverBlocks])

  useInitiativePageContentBlocksSave(initiative.id, serverBlocks, blocks)

  useEffect(() => {
    if (isOverviewPageContentStepValid(blocks)) {
      completeStep(initiativeCreationSteps.pageContent)
    } else {
      uncompleteStep(initiativeCreationSteps.pageContent)
    }
  }, [blocks, completeStep, uncompleteStep])

  return (
    <CreationGuide
      selectedPhaseType={selectedPhaseType}
      currentStep={initiativeCreationSteps.pageContent}
    >
      <Column gap="0.25rem" alignItems="stretch">
        <Text.Medium
          style={{
            color: 'var(--brand-secondary)',
            fontWeight: 500,
          }}
        >
          {t.pageContentTitle}
        </Text.Medium>
        <Text.Medium color="var(--palette-blue-500)">
          {enrichWithLinks(t.pageContentSubtitle)}
        </Text.Medium>

        <PageContentEditor
          value={blocks}
          minBlocks={1}
          onValueChange={setBlocks}
        />
      </Column>
    </CreationGuide>
  )
}
